import { css } from '@emotion/react';
import { semanticTokens, tokens, BREAKPOINTS } from '@flysas-tech/commercial-ui';
import { t } from 'i18n';
import { DurationLikeObject } from 'luxon';
import { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Flight } from 'shared';

import { DurationSegmentPopup, DurationLayoverPopup } from './DurationBarPopup';
import { FlightDurations, isSegmentDuration, mapFlightDuration } from './helpers';

function toMinutes(duration?: DurationLikeObject): number {
  if (!duration) return 0;
  return (duration.hours || 0) * 60 + (duration.minutes || 0);
}

const styles = {
  wrapper: css`
    position: relative;
    width: 100%;
  `,
  container: css`
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    width: 100%;
    position: relative;
  `,
  stop: (isLast: boolean) => css`
    font-size: ${tokens.fontSize.paragraph['paragraph-1']};
    font-weight: 700;
    position: absolute;
    top: 8px;
    left: ${!isLast ? '0' : 'auto'};
    right: ${isLast ? '0' : 'auto'};

    @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
      position: static;
      margin: 0;
      font-size: ${tokens.fontSize.paragraph['paragraph-2']};
    }
  `,
  raisedStop: css`
    font-size: ${tokens.fontSize.paragraph['paragraph-1']};
    font-weight: 700;
    position: absolute;
    top: 6px;
    white-space: nowrap;
    @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
      top: -18px;
      color: ${semanticTokens.color.text.secondary};
    }
  `,
  layoverBar: css`
    position: relative;
    width: 100%;
    height: 2px;
    background-color: ${tokens.color.turquoise['80']};
    border-radius: 1px;
    min-width: 2px;

    &::before,
    &::after {
      content: '';
      position: absolute;
      width: 2px;
      height: 8px;
      background-color: ${tokens.color.blue['30']};
      top: -3px;
    }
    &::before {
      left: 0;
      transform: translateX(-50%);
    }
    &::after {
      right: 0;
      transform: translateX(50%);
    }
  `,
  lineContainer: css`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    position: relative;
    width: 100%;
    margin: 0 8px;
  `,
  lineWrapper: css`
    display: flex;
    width: 100%;
    align-items: center;
    height: 100%;
    &::before,
    &::after {
      content: '';
      position: absolute;
      width: 6px;
      height: 6px;
      background-color: ${tokens.color.blue['30']};
      border-radius: 50%;
      top: 5px;
    }
    &::before {
      left: -2px;
    }
    &::after {
      right: -2px;
    }
  `,
  segmentBar: (widthPercent: number) => css`
    height: 2px;
    background-color: ${tokens.color.blue['40']};
    flex: 0 0 ${widthPercent}%;
    border-radius: 2px;
  `,
  layoverContainer: (widthPercent: number) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 0 0 ${widthPercent}%;
    min-width: 2px;
  `,
  layoverWithStop: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
  `,
  stopWrapper: css`
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
  `,
  operators: css`
    display: none;

    @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
      display: block;
      font-size: ${tokens.fontSize.paragraph['paragraph-1']};
      color: ${semanticTokens.color.text.secondary};
      margin: 0;
    }
  `,
  hoverMessage: (hoverPosition: number) => css`
    position: absolute;
    background: white;
    padding: 8px 12px;
    border-radius: ${tokens.dimension['space-4']};
    box-shadow: ${tokens.shadow.large};
    z-index: 10;
    pointer-events: none;
    bottom: 50px;
    left: ${hoverPosition}px;
    transform: translateX(-50%);
  `,
  hoverArrow: css`
    position: absolute;
    bottom: -${tokens.dimension['space-4']};
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: ${tokens.dimension['space-5']} solid transparent;
    border-right: ${tokens.dimension['space-5']} solid transparent;
    border-top: ${tokens.dimension['space-5']} solid white;
  `,
  bar: css`
    position: relative;
    width: 100%;
    padding: 7px 0;
  `,
};

export const FlightSegmentBar = ({ flight }: { flight: Flight }) => {
  const flightDurations = mapFlightDuration(flight);
  const segmentDurations = flightDurations.filter(isSegmentDuration);

  const origin = segmentDurations[0].departure;
  const destination = segmentDurations[segmentDurations.length - 1].arrival;

  const operatedBy = segmentDurations
    .reduce((operators: string[], segment) => {
      if (!operators.includes(segment.operatedBy)) {
        operators.push(segment.operatedBy);
      }
      return operators;
    }, [])
    .join(' & ');

  return (
    <div css={styles.wrapper} aria-hidden="true">
      <div css={styles.container}>
        <p css={styles.stop(false)}>{origin.code}</p>
        <FlightDurationBarSegments flightDurations={flightDurations} />
        <p css={styles.stop(true)}>{destination.code}</p>
      </div>
      <p css={styles.operators}>
        {t('operatedBy', {
          airline: operatedBy,
        })}{' '}
        {flight.isCodeShare && ' * '}
      </p>
    </div>
  );
};

export function FlightSegmentBarSkeleton() {
  return (
    <div css={styles.wrapper}>
      <div css={styles.container}>
        <div style={{ width: '100%' }}>
          <Skeleton height={8} width="100%" />
        </div>
      </div>

      <p css={styles.operators}>
        <Skeleton width={100} height={10} style={{ marginTop: '16px' }} />
      </p>
    </div>
  );
}

const DurationBar = ({
  children,
  popover,
  className,
}: {
  children?: React.ReactNode;
  popover: React.ReactNode;
  className?: string;
}) => {
  const [hoverPosition, setHoverPosition] = useState<number | undefined>(0);
  return (
    <div
      css={styles.bar}
      className={className}
      onMouseEnter={e => setHoverPosition(e.clientX - e.currentTarget.getBoundingClientRect().left)}
      onMouseLeave={() => setHoverPosition(undefined)}
    >
      {hoverPosition ? (
        <div css={styles.hoverMessage(hoverPosition)}>
          {popover}
          <div css={styles.hoverArrow}></div>
        </div>
      ) : null}
      {children}
    </div>
  );
};

function FlightDurationBarSegments({ flightDurations }: { flightDurations: FlightDurations }) {
  const total = flightDurations.reduce((acc, segment) => acc + toMinutes(segment.duration), 0);

  return (
    <div css={styles.lineContainer}>
      <div css={styles.lineWrapper}>
        {flightDurations.map((segment, index) =>
          segment.type === 'segment' ? (
            <DurationBar key={`segment-${index}`} popover={<DurationSegmentPopup {...segment} />}>
              <div css={styles.segmentBar((100 * toMinutes(segment.duration)) / total)} />
            </DurationBar>
          ) : (
            <DurationBar key={`layover-${index}`} popover={<DurationLayoverPopup {...segment} />}>
              <div css={styles.layoverContainer((100 * toMinutes(segment.duration)) / total)}>
                <div css={styles.raisedStop}>{segment.airport.code}</div>
                <div css={styles.layoverBar} />
              </div>
            </DurationBar>
          )
        )}
      </div>
    </div>
  );
}
